import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Moon, Sun, Search, Menu, X, ImageIcon, Heart, Globe } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useFavorites } from "@/context/FavoritesContext";
import { TOOLS } from "@/data/tools";
import { NAV } from "@/constants/testIds";

const NavItem = ({ to, children, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      `text-sm font-medium transition-colors ${isActive ? "text-blue-600 dark:text-blue-400" : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"}`
    }
  >
    {children}
  </NavLink>
);

export default function Header() {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const { lang, setLang, t } = useI18n();
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const suggestions = q
    ? TOOLS.filter((tt) => tt.name.toLowerCase().includes(q.toLowerCase())).slice(0, 6)
    : [];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (suggestions.length > 0) {
      navigate(`/tools/${suggestions[0].slug}`);
      setQ(""); setShowSuggest(false);
    } else {
      navigate(`/tools?q=${encodeURIComponent(q)}`);
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 dark:border-white/10 bg-white/80 dark:bg-[#08090a]/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 rounded-lg bg-blue-600 grid place-items-center text-white">
              <ImageIcon size={18} strokeWidth={2.5} />
            </div>
            <span className="font-semibold text-lg tracking-tight text-slate-900 dark:text-white">
              ImageKit<span className="text-blue-600 dark:text-blue-400">Hub</span>
            </span>
          </Link>

          <form onSubmit={handleSubmit} className="hidden md:block flex-1 max-w-md relative">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid={NAV.search}
                type="text"
                placeholder={t("nav.search_placeholder")}
                value={q}
                onChange={(e) => { setQ(e.target.value); setShowSuggest(true); }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                className="w-full bg-slate-50 dark:bg-[#1a1c20] border border-slate-200 dark:border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-colors"
              />
            </div>
            {showSuggest && suggestions.length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-white dark:bg-[#121316] border border-slate-200 dark:border-white/10 rounded-lg shadow-lg py-2 z-50">
                {suggestions.map((tt) => (
                  <button
                    type="button" key={tt.slug}
                    onMouseDown={() => { navigate(`/tools/${tt.slug}`); setQ(""); }}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5"
                  >
                    {tt.name}
                  </button>
                ))}
              </div>
            )}
          </form>

          <nav className="hidden lg:flex items-center gap-6">
            <NavItem to="/" testId={NAV.home}>{t("nav.home")}</NavItem>
            <NavItem to="/tools" testId={NAV.tools}>{t("nav.tools")}</NavItem>
            <NavItem to="/blog" testId={NAV.blog}>{t("nav.blog")}</NavItem>
            <NavItem to="/about" testId={NAV.about}>{t("nav.about")}</NavItem>
            <NavItem to="/contact" testId={NAV.contact}>{t("nav.contact")}</NavItem>
          </nav>

          <div className="flex items-center gap-1.5">
            <Link
              to="/favorites"
              data-testid="nav-favorites"
              aria-label={t("nav.favorites")}
              className="relative p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
            >
              <Heart size={18} />
              {favorites.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold grid place-items-center">
                  {favorites.length}
                </span>
              )}
            </Link>

            <div className="relative">
              <button
                onClick={() => setLangOpen((o) => !o)}
                onBlur={() => setTimeout(() => setLangOpen(false), 150)}
                data-testid="nav-lang-toggle"
                aria-label="Language"
                className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors flex items-center gap-1"
              >
                <Globe size={18} />
                <span className="text-xs font-medium">{lang.toUpperCase()}</span>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 w-32 bg-white dark:bg-[#121316] border border-slate-200 dark:border-white/10 rounded-lg shadow-lg py-1 z-50">
                  {[{ code: "en", label: "English" }, { code: "hi", label: "हिन्दी" }].map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-${l.code}`}
                      onMouseDown={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-white/5 ${lang === l.code ? "text-blue-600 dark:text-blue-400 font-medium" : "text-slate-700 dark:text-slate-200"}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={toggle}
              data-testid={NAV.themeToggle}
              aria-label="Toggle theme"
              className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {user?.is_admin ? (
              <Link to="/admin" className="hidden sm:inline-flex text-sm font-medium px-4 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                {t("nav.admin")}
              </Link>
            ) : (
              <Link
                to="/admin/login"
                data-testid={NAV.adminLogin}
                className="hidden sm:inline-flex text-sm font-medium px-4 py-2 rounded-full border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
              >
                {t("nav.signin")}
              </Link>
            )}
            <button
              className="lg:hidden p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10"
              onClick={() => setOpen((o) => !o)}
              data-testid={NAV.mobileMenu}
              aria-label="Menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden pb-4 space-y-1">
            <form onSubmit={handleSubmit} className="mb-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={t("nav.search_placeholder")}
                  className="w-full bg-slate-50 dark:bg-[#1a1c20] border border-slate-200 dark:border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm"
                />
              </div>
            </form>
            {[
              ["/", t("nav.home")], ["/tools", t("nav.tools")], ["/blog", t("nav.blog")],
              ["/favorites", t("nav.favorites")], ["/about", t("nav.about")], ["/contact", t("nav.contact")],
            ].map(([to, label]) => (
              <NavLink key={to} to={to} onClick={() => setOpen(false)}
                className={({ isActive }) => `block px-3 py-2 rounded-lg text-sm font-medium ${isActive ? "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300" : "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5"}`}>
                {label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
